// ---------------------------------------------------------------------------
// Hip-spine angle adapters that produce renderable overlay objects from a
// pixel-space LandmarkMap (Three.js Vector2 coords, post y-flip). Returns
// `HipSpineAngleOverlay` objects carrying display names, abbreviations, category, and
// landmark refs used by the 3D-planner overlay pipeline to draw arcs and rays.
// The actual geometric formulas live in `hipSpineAngleFormulas.ts`; for the
// scalar-number classification / nomogram pipeline see
// `hipSpineAngleMeasurements.ts`.
// ---------------------------------------------------------------------------

import { Vector2 } from 'three';
import { HipSpineLandmarkName } from './hipSpineLandmarks';
import {
    appTilt,
    lumbarLordosis,
    pelvicFemoralAngle,
    pelvicIncidence,
    sacralSlope,
} from './hipSpineAngleFormulas';

export type HipSpineAnnotationCategory = 'lumbar' | 'sacrum' | 'pelvis' | 'femoral';

type HipSpinePosture = 'standing-sagittal' | 'flexed-seated-sagittal' | 'lumbar-sagittal';

export const HipSpineAngleId = {
    LumbarLordosis: 'lumbar-lordosis',
    SacralSlope: 'sacral-slope',
    PelvicFemoral1: 'pelvic-femoral-1',
    PelvicFemoral2: 'pelvic-femoral-2',
    PelvicIncidence: 'pelvic-incidence',
    AppTilt: 'app-tilt',
} as const;

export type HipSpineAngleId = (typeof HipSpineAngleId)[keyof typeof HipSpineAngleId];

const landmarkName = HipSpineLandmarkName;

export type LandmarkMap = Map<string, Vector2>;

/**
 * Direction of ray2 (the reference ray) when {@link RayAngleRefs.ray2Landmarks}
 * is empty — i.e. the angle is measured against a fixed reference line rather
 * than a landmark-defined ray:
 * - `horizontal` — the x axis, signed to share ray1's x-direction so the arc
 *   sits on ray1's side regardless of which way the patient faces (e.g. sacral
 *   slope). This is the default.
 * - `vertical` — the y axis (e.g. APP tilt, measured against vertical).
 */
export type RayReference = 'horizontal' | 'vertical';

export type RayAngleRefs = {
    kind: 'ray';
    vertexLandmarks: string[];
    ray1Landmarks: string[];
    ray2Landmarks: string[];
    /**
     * Direction of the reference ray when `ray2Landmarks` is empty. Ignored when
     * `ray2Landmarks` is non-empty (ray2 then points at the averaged landmark).
     * Defaults to `horizontal`.
     */
    ray2Reference?: RayReference;
    extraDotLandmarks?: string[];
    /** When true, ray1 is the perpendicular bisector of the endplate defined by ray1Landmarks */
    perpendicularRay1?: boolean;
};

export type CurveAngleRefs = {
    kind: 'curve';
    endplate1: [string, string];
    endplate2: [string, string];
};

type AngleLandmarkRefs = RayAngleRefs | CurveAngleRefs;

/** Renderable angle overlay: the computed angle value plus the landmark refs needed to draw it. */
export type HipSpineAngleOverlay = {
    id: HipSpineAngleId;
    displayName: string;
    abbreviation: string;
    value: number;
    category: HipSpineAnnotationCategory;
    landmarkRefs: AngleLandmarkRefs;
};

function collectLandmarksOrNull(landmarks: LandmarkMap, ...names: string[]): Vector2[] | null {
    const result: Vector2[] = [];
    for (const n of names) {
        const p = landmarks.get(n);
        if (!p) return null;
        result.push(p);
    }
    return result;
}

// --- Angle calculators ---

function buildLumbarLordosisOverlay(
    landmarks: LandmarkMap,
    lumbarPair: [string, string],
): HipSpineAngleOverlay | null {
    const sacrum = collectLandmarksOrNull(landmarks, landmarkName.SacrumAnterior, landmarkName.SacrumPosterior);
    if (!sacrum) return null;
    const lumbar = collectLandmarksOrNull(landmarks, lumbarPair[0], lumbarPair[1]);
    if (!lumbar) return null;

    const degrees = lumbarLordosis(
        { a: sacrum[0], b: sacrum[1] },
        { a: lumbar[0], b: lumbar[1] },
    );

    return {
        id: HipSpineAngleId.LumbarLordosis,
        displayName: 'Lumbar Lordosis',
        abbreviation: 'LL',
        value: degrees,
        category: 'lumbar',
        landmarkRefs: {
            kind: 'curve',
            endplate1: [landmarkName.SacrumAnterior, landmarkName.SacrumPosterior],
            endplate2: [lumbarPair[0], lumbarPair[1]],
        },
    };
}

function buildSacralSlopeOverlay(landmarks: LandmarkMap): HipSpineAngleOverlay | null {
    const sacrum = collectLandmarksOrNull(landmarks, landmarkName.SacrumAnterior, landmarkName.SacrumPosterior);
    if (!sacrum) return null;

    const degrees = sacralSlope({ a: sacrum[0], b: sacrum[1] });

    return {
        id: HipSpineAngleId.SacralSlope,
        displayName: 'Sacral Slope',
        abbreviation: 'SS',
        value: degrees,
        category: 'sacrum',
        landmarkRefs: {
            kind: 'ray',
            vertexLandmarks: [landmarkName.SacrumAnterior, landmarkName.SacrumPosterior],
            ray1Landmarks: [landmarkName.SacrumPosterior, landmarkName.SacrumAnterior],
            ray2Landmarks: [],
            ray2Reference: 'horizontal',
        },
    };
}

function buildPelvicFemoralOverlay(landmarks: LandmarkMap, side: 1 | 2): HipSpineAngleOverlay | null {
    const hjcName = side === 1 ? landmarkName.HJC1 : landmarkName.HJC2;
    const cortexName = side === 1 ? landmarkName.FemurCortex1 : landmarkName.FemurCortex2;
    const points = collectLandmarksOrNull(landmarks, hjcName, cortexName, landmarkName.SacrumAnterior, landmarkName.SacrumPosterior);
    if (!points) return null;
    const [hjc, femurCortex, sacrumAnterior, sacrumPosterior] = points;

    const degrees = pelvicFemoralAngle(
        { a: sacrumAnterior, b: sacrumPosterior },
        hjc,
        femurCortex,
    );

    const id = side === 1 ? HipSpineAngleId.PelvicFemoral1 : HipSpineAngleId.PelvicFemoral2;
    return {
        id,
        displayName: `Pelvic Femoral Angle ${side}`,
        abbreviation: `PFA${side}`,
        value: degrees,
        category: 'femoral',
        landmarkRefs: {
            kind: 'ray',
            vertexLandmarks: [hjcName],
            ray1Landmarks: [hjcName, cortexName],
            ray2Landmarks: [landmarkName.SacrumAnterior, landmarkName.SacrumPosterior],
        },
    };
}

function buildPelvicIncidenceOverlay(landmarks: LandmarkMap): HipSpineAngleOverlay | null {
    const sacrum = collectLandmarksOrNull(landmarks, landmarkName.SacrumAnterior, landmarkName.SacrumPosterior);
    if (!sacrum) return null;
    const hjcs = collectLandmarksOrNull(landmarks, landmarkName.HJC1, landmarkName.HJC2);
    if (!hjcs) return null;
    const [hjc1, hjc2] = hjcs;

    const degrees = pelvicIncidence({ a: sacrum[0], b: sacrum[1] }, hjc1, hjc2);

    return {
        id: HipSpineAngleId.PelvicIncidence,
        displayName: 'Pelvic Incidence',
        abbreviation: 'PI',
        value: degrees,
        category: 'sacrum',
        landmarkRefs: {
            kind: 'ray',
            vertexLandmarks: [landmarkName.SacrumAnterior, landmarkName.SacrumPosterior],
            ray1Landmarks: [landmarkName.SacrumAnterior, landmarkName.SacrumPosterior],
            ray2Landmarks: [landmarkName.HJC1, landmarkName.HJC2],
            perpendicularRay1: true,
        },
    };
}

function buildAppTiltOverlay(landmarks: LandmarkMap): HipSpineAngleOverlay | null {
    const points = collectLandmarksOrNull(
        landmarks,
        landmarkName.ASIS1,
        landmarkName.ASIS2,
        landmarkName.PubicSymphysis,
        landmarkName.SacrumAnterior,
        landmarkName.SacrumPosterior,
    );
    if (!points) return null;
    const [asis1, asis2, pubicSymphysis, sacrumAnterior, sacrumPosterior] = points;

    const degrees = appTilt(
        { a: asis1, b: asis2 },
        pubicSymphysis,
        sacrumAnterior,
        sacrumPosterior,
    );

    return {
        id: HipSpineAngleId.AppTilt,
        displayName: 'Anterior Pelvic Plane Tilt',
        abbreviation: 'APPT',
        value: degrees,
        category: 'pelvis',
        landmarkRefs: {
            kind: 'ray',
            vertexLandmarks: [landmarkName.PubicSymphysis],
            ray1Landmarks: [landmarkName.PubicSymphysis, landmarkName.ASIS1, landmarkName.ASIS2],
            ray2Landmarks: [],
            ray2Reference: 'vertical',
            extraDotLandmarks: [landmarkName.ASIS1, landmarkName.ASIS2],
        },
    };
}

// --- Main entry point ---

export function computeAngleOverlays(
    landmarks: LandmarkMap,
    posture: HipSpinePosture,
    highestLumbarPair: string[],
): HipSpineAngleOverlay[] {
    const results: HipSpineAngleOverlay[] = [];

    if (highestLumbarPair.length === 2) {
        const lumbarLordosis = buildLumbarLordosisOverlay(landmarks, highestLumbarPair as [string, string]);
        if (lumbarLordosis) results.push(lumbarLordosis);
    }

    const sacralSlope = buildSacralSlopeOverlay(landmarks);
    if (sacralSlope) results.push(sacralSlope);

    if (posture === 'standing-sagittal') {
        const pelvicFemoral1 = buildPelvicFemoralOverlay(landmarks, 1);
        if (pelvicFemoral1) results.push(pelvicFemoral1);

        const pelvicFemoral2 = buildPelvicFemoralOverlay(landmarks, 2);
        if (pelvicFemoral2) results.push(pelvicFemoral2);

        const pelvicIncidence = buildPelvicIncidenceOverlay(landmarks);
        if (pelvicIncidence) results.push(pelvicIncidence);

        const appTilt = buildAppTiltOverlay(landmarks);
        if (appTilt) results.push(appTilt);
    }

    // Display ordering is alphanumeric by abbreviation (APPT, LL, PFA1, PFA2,
    // PI, SS), shared by both the Angles list and the drawn overlay groups.
    results.sort((a, b) => a.abbreviation.localeCompare(b.abbreviation));

    return results;
}
